import React, { useState, useEffect, useRef } from 'react';
import { PencilIcon, TrashIcon, SearchIcon, CalendarIcon, PlusIcon, DocumentDownloadIcon, ChevronLeftIcon, ChevronRightIcon, ExclamationCircleIcon, BanIcon } from '@heroicons/react/outline';
import { usePermissions } from '../../../contexts/PermissionsContext';
import { motion } from 'framer-motion';
import * as XLSX from 'xlsx';
import toastService from '../../../services/toast';
import usersService from '../../../services/users';
import FormModal from '../../../components/common/FormModal';
import AlertConfirmModal from '../../common/AlertConfirmModal';
import { ROLES } from '../../../services/permissionsService';

const TraineesPage = () => {
  const { hasPermission } = usePermissions();
  const searchTimeoutRef = useRef(null);
  const [trainees, setTrainees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searching, setSearching] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalTrainees, setTotalTrainees] = useState(0);
  const [totalBanned, setTotalBanned] = useState(0);
  const [lastPage, setLastPage] = useState(1);
  const [showAddModal, setShowAddModal] = useState(false);
  const [availableRoles, setAvailableRoles] = useState([]); // قائمة الأدوار المتاحة من السيرفر
  const [newTrainee, setNewTrainee] = useState({
    name: '',
    email: '',
    phone: '',
    password: '',
    status: 'نشط',
    role_id: null
  });
  const [newPhoneError, setNewPhoneError] = useState('');
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedTrainee, setSelectedTrainee] = useState(null);
  const [formErrors, setFormErrors] = useState({});
  const [phoneError, setPhoneError] = useState('');
  const [showBanModal, setShowBanModal] = useState(false);
  const [traineeToBlock, setTraineeToBlock] = useState(null);
  const dateFromRef = useRef(null);
  const dateToRef = useRef(null);

  useEffect(() => {
    return () => { if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current); };
  }, []);

  const loadStats = async () => {
    try {
      const stats = await usersService.fetchTraineeStats();
      setTotalTrainees(stats.total);
      setTotalBanned(stats.total_banned);
    } catch (e) {
    }
  };

  const fetchAvailableRoles = async () => {
    try {
      const rolesData = await usersService.fetchRoles();
      if (rolesData && Array.isArray(rolesData.data)) {
        setAvailableRoles(rolesData.data);
        if (rolesData.data.length > 0) {
          setNewTrainee(prev => ({
            ...prev,
            role: rolesData.data.find(role => role.name === ROLES.TRAINEE)?.name || rolesData.data[0].name
          }));
        }
      }
    } catch (error) {
      toastService.error('حدث خطأ في جلب الأدوار المتاحة');
    }
  };

  useEffect(() => {
    fetchAvailableRoles();
    loadTrainees(1);
    loadStats();
  }, []);

  const loadTrainees = async (page, search = null, from = null, to = null) => {
    setLoading(true);
    try {
      const params = { page, per_page: 20 };
      if (search) params.search = search;
      if (from) params.date_from = from;
      if (to) params.date_to = to;
      const response = await usersService.fetchTrainees(params);
      setTrainees(response.data);
      setLastPage(response.last_page);
      setCurrentPage(response.current_page);
    } catch (error) {
      toastService.error(
        <div className="text-right" dir="rtl">
          <p className="font-bold mb-2">حدث خطأ:</p>
          <ul className="list-disc mr-5">
            {error.split('\n').map((err, index) => (
              <li key={index} className="text-right">{err}</li>
            ))}
          </ul>
        </div>
      );
    } finally {
      setLoading(false);
    }
  };

  const handleSearchChange = (value) => {
    setSearchQuery(value);
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    if (!value.trim()) { setSearching(false); loadTrainees(1); return; }
    setSearching(true);
    searchTimeoutRef.current = setTimeout(() => {
      loadTrainees(1, value.trim(), dateFrom, dateTo);
      setSearching(false);
    }, 300);
  };

  const handleDateFromChange = (e) => {
    const value = e.target.value;
    setDateFrom(value);
    setCurrentPage(1);
    loadTrainees(1, searchQuery, value, dateTo);
  };

  const handleDateToChange = (e) => {
    const value = e.target.value;
    setDateTo(value);
    setCurrentPage(1);
    loadTrainees(1, searchQuery, dateFrom, value);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
    loadTrainees(page, searchQuery, dateFrom, dateTo);
  };

  const handleAddTrainee = async (e) => {
    // التحقق من وجود الحدث قبل استخدام preventDefault
    if (e && typeof e.preventDefault === 'function') {
      e.preventDefault();
    }
    
    try {
      const response = await usersService.createTrainee(newTrainee);
      toastService.success('تم إضافة المستخدم بنجاح');
      setShowAddModal(false);
      setNewTrainee({
        name: '',
        email: '',
        phone: '',
        password: '',
        status: 'نشط',
        role_id: null
      });
      
      // تحديث داخلي للقائمة بدلاً من إعادة التحميل الكامل
      if (response.data && response.data.user) {
        // إضافة المستخدم الجديد إلى القائمة
        setTrainees(prevTrainees => [response.data.user, ...prevTrainees]);
      } else {
        // في حالة عدم استجابة المستخدم بالشكل المتوقع، نقوم بإعادة التحميل
        loadTrainees(1);
      }
    } catch (error) {
      toastService.error(
        <div className="text-right" dir="rtl">
          <p className="font-bold mb-2">حدث خطأ:</p>
          <ul className="list-disc mr-5">
            {error.split('\n').map((err, index) => (
              <li key={index} className="text-right">{err}</li>
            ))}
          </ul>
        </div>
      );
    }
  };

  const handleUpdateTrainee = async () => {
    try {
      const { id, ...traineeData } = selectedTrainee;
      const response = await usersService.updateTrainee(id, traineeData);
      toastService.success('تم تحديث بيانات المتدرب بنجاح');
      setShowEditModal(false);
      
      // تحقق إذا تم تغيير الدور
      const isRoleChanged = response.data?.user?.role !== ROLES.TRAINEE;
      
      if (isRoleChanged) {
        // إذا تغير الدور إلى شيء غير متدرب، نحذف المستخدم من القائمة
        setTrainees(prevTrainees => prevTrainees.filter(trainee => trainee.id !== id));
      } else {
        // إذا لم يتغير الدور أو لم نتمكن من التحقق، نحدث بيانات المستخدم
        if (response.data && response.data.user) {
          // تحديث المستخدم في القائمة باستخدام البيانات من السيرفر
          setTrainees(prevTrainees => 
            prevTrainees.map(trainee => 
              trainee.id === id ? response.data.user : trainee
            )
          );
        } else {
          // حتى لو ما رجعش بيانات من السيرفر، نحدث القائمة بالبيانات المرسلة
          setTrainees(prevTrainees => 
            prevTrainees.map(trainee => 
              trainee.id === id ? { ...trainee, ...traineeData } : trainee
            )
          );
        }
      }
      
      setSelectedTrainee(null);
    } catch (error) {
      toastService.error(
        <div className="text-right" dir="rtl">
          <p className="font-bold mb-2">حدث خطأ:</p>
          <ul className="list-disc mr-5">
            {error.split('\n').map((err, index) => (
              <li key={index} className="text-right">{err}</li>
            ))}
          </ul>
        </div>
      );
    }
  };

  const handleDeleteTrainee = async () => {
    try {
      const idToDelete = selectedTrainee.id;
      await usersService.deleteTrainee(idToDelete);
      toastService.success('تم حذف المتدرب بنجاح');
      setShowDeleteModal(false);
      setSelectedTrainee(null);
      
      // تحديث داخلي للقائمة بحذف المتدرب منها
      setTrainees(prevTrainees => 
        prevTrainees.filter(trainee => trainee.id !== idToDelete)
      );
    } catch (error) {
      toastService.error(
        <div className="text-right" dir="rtl">
          <p className="font-bold mb-2">حدث خطأ:</p>
          <ul className="list-disc mr-5">
            {error.split('\n').map((err, index) => (
              <li key={index} className="text-right">{err}</li>
            ))}
          </ul>
        </div>
      );
    }
  };

  const handleChangeStatus = async () => {
    try {
      const idToUpdate = traineeToBlock.id;
      const newStatus = traineeToBlock.status !== 'محظور' ? 'محظور' : 'نشط';
      const response = await usersService.changeTraineeStatus(idToUpdate, traineeToBlock.status !== 'محظور');
      toastService.success(`تم ${traineeToBlock.status === 'محظور' ? 'إلغاء حظر' : 'حظر'} المتدرب بنجاح`);
      setShowBanModal(false);
      
      // تحديث داخلي للقائمة بتغيير حالة المتدرب
      if (response.data && response.data.user) {
        // تحديث المستخدم في القائمة
        setTrainees(prevTrainees => 
          prevTrainees.map(trainee => 
            trainee.id === idToUpdate ? response.data.user : trainee
          )
        );
      } else {
        // تحديث الحالة بناءً على المعلومات المتوفرة
        setTrainees(prevTrainees => 
          prevTrainees.map(trainee => 
            trainee.id === idToUpdate ? {...trainee, status: newStatus} : trainee
          )
        );
      }
      
      setTraineeToBlock(null);
    } catch (error) {
      toastService.error(
        <div className="text-right" dir="rtl">
          <p className="font-bold mb-2">حدث خطأ:</p>
          <ul className="list-disc mr-5">
            {error.split('\n').map((err, index) => (
              <li key={index} className="text-right">{err}</li>
            ))}
          </ul>
        </div>
      );
    }
  };

  const handlePreviousPage = () => {
    if (currentPage > 1) handlePageChange(currentPage - 1);
  };

  const handleNextPage = () => {
    if (currentPage < lastPage) handlePageChange(currentPage + 1);
  };

  const handleExportToExcel = () => {
    // تحويل البيانات إلى تنسيق مناسب للإكسل
    const dataToExport = trainees.map(trainee => ({
      'الاسم': trainee.name,
      'البريد الإلكتروني': trainee.email,
      'رقم الجوال': trainee.phone,
      'تاريخ التسجيل': trainee.createdAt ? trainee.createdAt.split('-').map((part, index) => 
        index === 2 ? part : part.padStart(2, '0')).join('-') : '',
      'الحالة': trainee.status
    }));

    // إنشاء ورقة عمل جديدة
    const ws = XLSX.utils.json_to_sheet(dataToExport);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "المتدربين");

    // تحميل الملف
    XLSX.writeFile(wb, "trainees-report.xlsx");
  };

  const handleAdd = async () => {
    const errors = [];
    
    if (!newTrainee.name?.trim()) {
      errors.push("الرجاء إدخال اسم المتدرب");
    }

    if (!newTrainee.email?.trim()) {
      errors.push("الرجاء إدخال البريد الإلكتروني");
    } else if (!/\S+@\S+\.\S+/.test(newTrainee.email)) {
      errors.push("البريد الإلكتروني غير صالح");
    }

    if (!newTrainee.phone?.trim()) {
      errors.push("الرجاء إدخال رقم الجوال");
    } else if (!/^\d+$/.test(newTrainee.phone)) {
      errors.push("رقم الجوال يجب أن يحتوي على أرقام فقط");
    } else if (newTrainee.phone.length !== 10) {
      errors.push("رقم الجوال يجب أن يتكون من 10 أرقام");
    } else if (!newTrainee.phone.startsWith('05')) {
      errors.push("رقم الجوال يجب أن يبدأ بـ 05");
    }

    if (!newTrainee.password?.trim()) {
      errors.push("الرجاء إدخال كلمة المرور");
    }

    if (errors.length > 0) {
      toastService.error(
        <div className="text-right" dir="rtl">
          <p className="font-bold mb-2">يرجى تصحيح الأخطاء التالية:</p>
          <ul className="list-disc mr-5">
            {errors.map((error, index) => (
              <li key={index} className="text-right">{error}</li>
            ))}
          </ul>
        </div>
      );
      return;
    }

    handleAddTrainee();
  };

  // دالة لفتح نافذة التعديل
  const handleEditClick = (trainee) => {
    // نسخة من المتدرب لكن بدون دور محدد
    const traineeWithEmptyRole = {
      ...trainee,
      role_id: '', // تفريغ الدور
      role: '',    // تفريغ الدور
    };
    
    setSelectedTrainee(traineeWithEmptyRole);
    setShowEditModal(true);
  };

  // دالة لفتح نافذة تأكيد الحذف
  const handleDeleteClick = (trainee) => {
    setSelectedTrainee(trainee);
    setShowDeleteModal(true);
  };

  // دالة للتحقق من صحة البيانات
  const validateForm = (trainee) => {
    const errors = {};
    
    // التحقق من وجود الاسم
    if (!trainee?.name || trainee.name.trim() === '') {
      errors.name = 'الاسم مطلوب';
    }

    // التحقق من وجود البريد الإلكتروني
    if (!trainee?.email || trainee.email.trim() === '') {
      errors.email = 'البريد الإلكتروني مطلوب';
    } else if (!/\S+@\S+\.\S+/.test(trainee.email)) {
      errors.email = 'البريد الإلكتروني غير صالح';
    }

    // التحقق من وجود رقم الجوال
    if (!trainee?.phone || trainee.phone.trim() === '') {
      errors.phone = 'رقم الجوال مطلوب';
    }

    // التحقق من وجود الدور
    if (!trainee?.role) {
      errors.role = 'الدور مطلوب';
    }

    return errors;
  };

  // دالة لحفظ التعديلات
  const handleEdit = async () => {
    const errors = validateForm(selectedTrainee);
    
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }

    handleUpdateTrainee();
  };

  // دالة لتأكيد الحذف
  const handleConfirmDelete = () => {
    handleDeleteTrainee();
  };

  // دالة لفتح نافذة تأكيد الحظر
  const handleBanClick = (trainee) => {
    setTraineeToBlock(trainee);
    setShowBanModal(true);
  };

  // دالة لتأكيد الحظر
  const handleConfirmBan = async () => {
    handleChangeStatus();
  };

  // تم إزالة loading spinner للصفحة كاملة واستبداله بـ loading skeletons للجدول فقط

  return (
    <div className="p-6">
      {/* Search and Filter Card */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg mb-6">
        {/* Search Header */}
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center">
          <h2 className="text-lg font-medium text-gray-900 dark:text-white">قائمة المتدربين</h2>
          <div className="flex gap-4">
            {hasPermission && hasPermission('create_trainee') && (
              <div className="relative group">
                <PlusIcon 
                  className="h-6 w-6 text-blue-600 hover:text-blue-700 dark:text-blue-500 dark:hover:text-blue-400 cursor-pointer transition-colors duration-200"
                  onClick={() => setShowAddModal(true)}
                />
                <div className="absolute invisible opacity-0 group-hover:visible group-hover:opacity-100 group-hover:delay-700 delay-0 -left-4 -translate-x-1/2 mt-2 px-2 py-1 text-sm bg-gray-900 text-white rounded shadow-lg whitespace-nowrap z-50 transition-all duration-75">
                  إضافة متدرب جديد
                </div>
              </div>
            )}
            
            <div className="relative group">
              <DocumentDownloadIcon 
                className="h-6 w-6 text-green-600 hover:text-green-700 dark:text-green-500 dark:hover:text-green-400 cursor-pointer transition-colors duration-200"
                onClick={handleExportToExcel}
              />
              <div className="absolute invisible opacity-0 group-hover:visible group-hover:opacity-100 group-hover:delay-700 delay-0 -left-4 -translate-x-1/2 mt-2 px-2 py-1 text-sm bg-gray-900 text-white rounded shadow-lg whitespace-nowrap z-50 transition-all duration-75">
                استخراج في ملف إكسل
              </div>
            </div>
          </div>
        </div>
        
        {/* Search Content */}
        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Search Input */}
            <div className="relative">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">البحث عن متدرب</label>
                <div className="relative rounded-lg">
                <input
                  type="text"
                  placeholder="البحث عن متدرب..."
                  value={searchQuery}
                  onChange={(e) => handleSearchChange(e.target.value)}
                  className="input-base pr-10"
                />
                <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                  {searching ? (
                    <svg className="animate-spin h-5 w-5 text-blue-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                    </svg>
                  ) : (
                    <SearchIcon className="h-5 w-5 text-blue-500 dark:text-blue-400" />
                  )}
                </div>
              </div>
            </div>

            {/* Date From */}
            <div className="relative">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">من تاريخ</label>
              <div className="relative rounded-lg">
                <input
                  type="date"
                  value={dateFrom}
                  onChange={handleDateFromChange}
                  className="input-base pr-10 [&::-webkit-calendar-picker-indicator]:opacity-0 [&::-webkit-calendar-picker-indicator]:absolute [&::-webkit-calendar-picker-indicator]:right-0 [&::-webkit-calendar-picker-indicator]:w-10"
                  ref={(input) => {
                    if (input) {
                      dateFromRef.current = input;
                    }
                  }}
                />
                <div 
                  className="absolute inset-y-0 right-0 pr-3 flex items-center cursor-pointer"
                  onClick={() => dateFromRef.current?.showPicker()}
                >
                  <CalendarIcon className="h-5 w-5 text-green-500 dark:text-green-400" />
                </div>
              </div>
            </div>

            {/* Date To */}
            <div className="relative">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">إلى تاريخ</label>
              <div className="relative rounded-lg">
                <input
                  type="date"
                  value={dateTo}
                  onChange={handleDateToChange}
                  className="input-base pr-10 [&::-webkit-calendar-picker-indicator]:opacity-0 [&::-webkit-calendar-picker-indicator]:absolute [&::-webkit-calendar-picker-indicator]:right-0 [&::-webkit-calendar-picker-indicator]:w-10"
                  ref={(input) => {
                    if (input) {
                      dateToRef.current = input;
                    }
                  }}
                />
                <div 
                  className="absolute inset-y-0 right-0 pr-3 flex items-center cursor-pointer"
                  onClick={() => dateToRef.current?.showPicker()}
                >
                  <CalendarIcon className="h-5 w-5 text-orange-500 dark:text-orange-400" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-4">
          <div className="text-sm font-medium text-gray-500 dark:text-gray-400">إجمالي المتدربين</div>
          <div className="mt-1 text-2xl font-semibold text-gray-900 dark:text-white">{totalTrainees}</div>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-4">
          <div className="text-sm font-medium text-gray-500 dark:text-gray-400">المتدربين الغير نشطين</div>
          <div className="mt-1 text-2xl font-semibold text-yellow-600 dark:text-yellow-400">
            {trainees.filter(t => t.status === 'غير نشط').length}
          </div>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-4">
          <div className="text-sm font-medium text-gray-500 dark:text-gray-400">المتدربين المحظورين</div>
          <div className="mt-1 text-2xl font-semibold text-red-600 dark:text-red-400">
            {totalBanned}
          </div>
        </div>
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg overflow-hidden">
        <div className={`${trainees.length > 0 ? 'overflow-x-auto' : ''}`}>
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700 text-center" style={{ tableLayout: 'fixed' }}>
            <thead className="bg-gray-50 dark:bg-gray-700">
              <tr>
                <th scope="col" className="w-40 px-6 py-4 text-sm font-semibold text-gray-900 dark:text-gray-100">
                  الاسم
                </th>
                <th scope="col" className="w-44 px-6 py-4 text-sm font-semibold text-gray-900 dark:text-gray-100">
                  البريد الإلكتروني
                </th>
                <th scope="col" className="w-40 px-6 py-4 text-sm font-semibold text-gray-900 dark:text-gray-100">
                  رقم الجوال
                </th>
                <th scope="col" className="w-40 px-6 py-4 text-sm font-semibold text-gray-900 dark:text-gray-100">
                  تاريخ التسجيل
                </th>
                <th scope="col" className="w-32 px-6 py-4 text-sm font-semibold text-gray-900 dark:text-gray-100">
                  الحالة
                </th>
                <th scope="col" className="w-24 px-6 py-4 text-sm font-semibold text-gray-900 dark:text-gray-100">
                  الإجراءات
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 bg-white dark:divide-gray-700 dark:bg-gray-800">
              {loading ? (
                [...Array(5)].map((_, i) => (
                  <tr key={`skeleton-${i}`} className="animate-pulse">
                    <td className="whitespace-nowrap px-6 py-4 text-center">
                      <div className="h-4 bg-gray-200 dark:bg-gray-600 rounded" />
                    </td>
                    <td className="whitespace-nowrap px-6 py-4 text-center">
                      <div className="h-4 bg-gray-200 dark:bg-gray-600 rounded" />
                    </td>
                    <td className="whitespace-nowrap px-6 py-4 text-center">
                      <div className="h-4 bg-gray-200 dark:bg-gray-600 rounded" />
                    </td>
                    <td className="whitespace-nowrap px-6 py-4 text-center">
                      <div className="h-4 bg-gray-200 dark:bg-gray-600 rounded" />
                    </td>
                    <td className="whitespace-nowrap px-6 py-4 text-center">
                      <div className="h-4 bg-gray-200 dark:bg-gray-600 rounded" />
                    </td>
                    <td className="whitespace-nowrap px-6 py-4 text-center">
                      <div className="flex gap-3 justify-center">
                        <div className="h-5 w-5 bg-gray-200 dark:bg-gray-600 rounded-full" />
                        <div className="h-5 w-5 bg-gray-200 dark:bg-gray-600 rounded-full" />
                        <div className="h-5 w-5 bg-gray-200 dark:bg-gray-600 rounded-full" />
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                trainees.map((trainee) => (
                  <tr key={trainee.id}>
                    <td className="whitespace-nowrap px-6 py-4 text-center">
                      <div className="text-sm text-gray-900 dark:text-gray-100">{trainee.name}</div>
                    </td>
                    <td className="whitespace-nowrap px-6 py-4 text-center">
                      <div className="text-sm text-gray-900 dark:text-gray-100">{trainee.email}</div>
                    </td>
                    <td className="whitespace-nowrap px-6 py-4 text-center">
                      <div className="text-sm text-gray-900 dark:text-gray-100" dir="ltr">
                        {trainee.phone}
                      </div>
                    </td>
                    <td className="whitespace-nowrap px-6 py-4 text-center">
                      <div className="text-sm text-gray-900 dark:text-gray-100" dir="ltr">
                        {trainee.createdAt.split('-').map((part, index) => 
                          index === 2 ? part : part.padStart(2, '0')).join('-')}
                      </div>
                    </td>
                    <td className="whitespace-nowrap px-6 py-4 text-center">
                      <span className={`inline-flex rounded-full px-2 text-xs font-semibold leading-5 ${
                        trainee.status === 'نشط' 
                          ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
                          : trainee.status === 'غير نشط'
                          ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400'
                          : 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400'
                      }`}>
                        {trainee.status}
                      </span>
                    </td>
                    <td className="whitespace-nowrap px-6 py-4 text-center">
                      <div className="flex gap-3 justify-center">
                        {hasPermission && hasPermission('edit_trainee') && (
                          <div className="relative group">
                            <BanIcon 
                              className={`h-5 w-5 cursor-pointer ${
                                trainee.status === 'محظور'
                                  ? 'text-green-600 hover:text-green-700 dark:text-green-500 dark:hover:text-green-400'
                                  : 'text-yellow-600 hover:text-yellow-700 dark:text-yellow-500 dark:hover:text-yellow-400'
                              }`}
                              onClick={() => handleBanClick(trainee)}
                            />
                            <div className="absolute invisible opacity-0 group-hover:visible group-hover:opacity-100 bottom-full mb-2 left-1/2 -translate-x-1/2 px-2 py-1 text-xs bg-gray-900 text-white rounded shadow-lg whitespace-nowrap z-50">
                              {trainee.status === 'محظور' ? 'إلغاء حظر المتدرب' : 'حظر المتدرب'}
                            </div>
                          </div>
                        )}
                        {hasPermission && hasPermission('edit_trainee') && (
                          <PencilIcon 
                            className="h-5 w-5 text-blue-600 hover:text-blue-700 dark:text-blue-500 dark:hover:text-blue-400 cursor-pointer" 
                            onClick={() => handleEditClick(trainee)}
                          />
                        )}
                        {hasPermission && hasPermission('delete_trainee') && (
                          <TrashIcon 
                            className="h-5 w-5 text-red-600 hover:text-red-700 dark:text-red-500 dark:hover:text-red-400 cursor-pointer" 
                            onClick={() => handleDeleteClick(trainee)}
                          />
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
            <tfoot className="bg-gray-50 dark:bg-gray-700 border-t border-gray-200 dark:border-gray-600">
              <tr>
                <td colSpan="7">
                  <div className="flex justify-between items-center mt-2 mb-2">
                    <div className="flex justify-center items-center mx-auto">
                      <button 
                        className="p-2 text-blue-600 hover:text-blue-800 transition-colors duration-200"
                        onClick={handlePreviousPage}
                        disabled={currentPage === 1}
                      >
                        <ChevronRightIcon className="h-6 w-6" />
                      </button>
                      <span className="mx-2 text-sm font-semibold text-gray-900 dark:text-gray-100">
                        {currentPage} / {lastPage}
                      </span>
                      <button 
                        className="p-2 text-blue-600 hover:text-blue-800 transition-colors duration-200"
                        onClick={handleNextPage}
                        disabled={currentPage === lastPage}
                      >
                        <ChevronLeftIcon className="h-6 w-6" />
                      </button>
                    </div>
                  </div>
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>
      {/* نافذة منبثقة لإضافة متدرب جديد */}
      {showAddModal && (
        <FormModal
          isOpen={showAddModal}
          onClose={() => {
            setShowAddModal(false);
            setNewTrainee({
              name: '',
              email: '',
              phone: '',
              password: '',
              status: 'نشط',
              role: availableRoles.find(role => role.name === ROLES.TRAINEE)?.name || (availableRoles[0]?.name || '')
            });
            setNewPhoneError('');
          }}
          onSubmit={handleAdd}
          title="إضافة متدرب جديد"
          submitText="إضافة"
        >
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              الاسم <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={newTrainee.name}
              onChange={(e) => setNewTrainee({...newTrainee, name: e.target.value})}
              className="input-base"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              البريد الإلكتروني <span className="text-red-500">*</span>
            </label>
            <input
              type="email"
              value={newTrainee.email}
              onChange={(e) => setNewTrainee({...newTrainee, email: e.target.value})}
              className="input-base"
              dir="ltr"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              رقم الجوال <span className="text-red-500">*</span>
            </label>
            <input
              type="tel"
              maxLength="10"
              value={newTrainee.phone}
              onChange={(e) => {
                const value = e.target.value.replace(/\D/g, '');
                if (value === '') {
                  setNewTrainee({...newTrainee, phone: value});
                  setNewPhoneError('');
                } else if (value.length <= 2) {
                  setNewTrainee({...newTrainee, phone: value});
                  if (value.length === 2 && value !== '05') {
                    setNewPhoneError('رقم الجوال يجب أن يبدأ بـ 05');
                  } else {
                    setNewPhoneError('');
                  }
                } else if (value.startsWith('05')) {
                  setNewTrainee({...newTrainee, phone: value});
                  setNewPhoneError('');
                }
              }}
              className={`input-base ${newPhoneError ? 'border-red-500' : ''}`}
              placeholder="05xxxxxxxx"
              dir="ltr"
            />
            {newPhoneError && (
              <p className="mt-1 text-sm text-red-500">{newPhoneError}</p>
            )}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              الدور <span className="text-red-500">*</span>
            </label>
            <select
              value={newTrainee.role_id || ''}
              onChange={(e) => setNewTrainee({...newTrainee, role_id: parseInt(e.target.value)})}
              className="input-base"
            >
              {availableRoles.map((role) => (
                <option key={role.id} value={role.id}>
                  {role.display_name || role.name}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              كلمة المرور <span className="text-red-500">*</span>
            </label>
            <input
              type="password"
              value={newTrainee.password}
              onChange={(e) => setNewTrainee({...newTrainee, password: e.target.value})}
              className="input-base"
            />
          </div>
        </FormModal>
      )}
      {/* نافذة تأكيد الحذف */}
      {showDeleteModal && selectedTrainee && (
        <AlertConfirmModal
          isOpen={showDeleteModal}
          onClose={() => {
            setShowDeleteModal(false);
            setSelectedTrainee(null);
          }}
          onConfirm={handleDeleteTrainee}
          icon={ExclamationCircleIcon}
          iconColorClass="text-red-500"
          title="تأكيد حذف المتدرب"
          message="هل أنت متأكد من حذف المتدرب؟"
          details={selectedTrainee.name}
          note="سيتم حذف المتدرب نهائياً من النظام"
          confirmText="تأكيد الحذف"
          cancelText="إلغاء"
          confirmButtonClass="bg-red-600 hover:bg-red-700"
          noteClass="text-red-600 dark:text-red-400"
        />
      )}

      {/* نافذة تعديل بيانات المتدرب */}
      {showEditModal && selectedTrainee && (
        <FormModal
          isOpen={showEditModal}
          onClose={() => {
            setShowEditModal(false);
            setSelectedTrainee(null);
            setPhoneError('');
          }}
          onSubmit={async () => {
            try {
              const { id, password, ...traineeData } = selectedTrainee;
              
              // تأكد من أن role_id موجود وصالح
              if (traineeData.role_id) {
                traineeData.role_id = parseInt(traineeData.role_id);
                const selectedRole = availableRoles.find(role => role.id === traineeData.role_id);
                if (selectedRole) {
                  traineeData.role = selectedRole.name;
                }
              }
              
              // 1. تحديث بيانات المتدرب الأساسية (بدون كلمة المرور)
              const response = await usersService.updateTrainee(id, traineeData);
              
              // 2. لو فيه كلمة مرور جديدة، استدعي API المخصصة لتغيير الباسورد للمتدربين
              if (password && password.trim() !== '') {
                // Change trainee password
                await usersService.changeTraineePassword(id, { password });
                // Password changed successfully
              }
              
              toastService.success('تم تحديث بيانات المتدرب بنجاح');
              setShowEditModal(false);
              
              // تحديث القائمة بعد التعديل
              if (response.data && response.data.user) {
                setTrainees(prevTrainees => prevTrainees.map(trainee => trainee.id === id ? response.data.user : trainee));
              } else {
                setTrainees(prevTrainees => prevTrainees.map(trainee => trainee.id === id ? { ...trainee, ...traineeData } : trainee));
              }
            } catch (error) {
              // Handle trainee update error
              toastService.error('حدث خطأ أثناء تحديث بيانات المتدرب');
            }
          }}
          title="تعديل بيانات المتدرب"
          submitText="حفظ"
          cancelText="إلغاء"
        >
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              الاسم <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={selectedTrainee.name}
              onChange={(e) => setSelectedTrainee({...selectedTrainee, name: e.target.value})}
              className="input-base"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              البريد الإلكتروني <span className="text-red-500">*</span>
            </label>
            <input
              type="email"
              value={selectedTrainee.email}
              onChange={(e) => setSelectedTrainee({...selectedTrainee, email: e.target.value})}
              className="input-base"
              dir="ltr"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              رقم الجوال <span className="text-red-500">*</span>
            </label>
            <input
              type="tel"
              maxLength="10"
              value={selectedTrainee.phone}
              onChange={(e) => {
                const value = e.target.value.replace(/\D/g, '');
                if (value === '') {
                  setSelectedTrainee({...selectedTrainee, phone: value});
                  setPhoneError('');
                } else if (value.length <= 2) {
                  setSelectedTrainee({...selectedTrainee, phone: value});
                  if (value.length === 2 && value !== '05') {
                    setPhoneError('رقم الجوال يجب أن يبدأ بـ 05');
                  } else {
                    setPhoneError('');
                  }
                } else if (value.startsWith('05')) {
                  setSelectedTrainee({...selectedTrainee, phone: value});
                  setPhoneError('');
                }
              }}
              className={`input-base ${phoneError ? 'border-red-500' : ''}`}
              placeholder="05xxxxxxxx"
              dir="ltr"
            />
            {phoneError && (
              <p className="mt-1 text-sm text-red-500">{phoneError}</p>
            )}
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              الدور <span className="text-red-500">*</span>
            </label>
            <select
              value={selectedTrainee.role_id || ''}
              onChange={(e) => {
                // التحقق من أن القيمة ليست فارغة قبل التحويل إلى رقم
                const roleId = e.target.value ? parseInt(e.target.value) : '';
                setSelectedTrainee({...selectedTrainee, role_id: roleId})
              }}
              className="input-base"
            >
              <option value="">اختر الدور المناسب</option>
              {availableRoles.map((role) => (
                <option key={role.id} value={role.id}>
                  {role.display_name || role.name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label>
              كلمة المرور (اختياري)
            </label>
            <input
              type="password"
              value={selectedTrainee.password || ''}
              onChange={(e) => setSelectedTrainee({...selectedTrainee, password: e.target.value})}
              placeholder="اتركها فارغة للاحتفاظ بكلمة المرور الحالية"
              className="input-base"
            />
          </div>

        </FormModal>
      )}
      {/* نافذة تأكيد الحظر */}
      {showBanModal && traineeToBlock && (
        <AlertConfirmModal
          isOpen={showBanModal}
          onClose={() => {
            setShowBanModal(false);
            setTraineeToBlock(null);
          }}
          onConfirm={handleChangeStatus}
          icon={BanIcon}
          iconColorClass={traineeToBlock.status === 'محظور' ? 'text-green-500' : 'text-yellow-500'}
          title={traineeToBlock.status === 'محظور' ? 'تأكيد فك حظر المتدرب' : 'تأكيد حظر المتدرب'}
          message={traineeToBlock.status === 'محظور' ? 'هل أنت متأكد من فك حظر المتدرب؟' : 'هل أنت متأكد من حظر المتدرب؟'}
          details={traineeToBlock.name}
          note={traineeToBlock.status === 'محظور' ? 'سيتمكن المتدرب من الوصول إلى النظام' : 'سيتم منع المتدرب من الوصول إلى النظام'}
          confirmText={traineeToBlock.status === 'محظور' ? 'تأكيد فك الحظر' : 'تأكيد الحظر'}
          cancelText="إلغاء"
          confirmButtonClass={traineeToBlock.status === 'محظور' ? 'bg-green-600 hover:bg-green-700' : 'bg-yellow-600 hover:bg-yellow-700'}
          noteClass={traineeToBlock.status === 'محظور' ? 'text-green-600 dark:text-green-400' : 'text-yellow-600 dark:text-yellow-400'}
        />
      )}
    </div>
  );
};

export default TraineesPage;
